import { reportUnlinkedException } from '../error-reporting/report-unlinked-exception'
/**
 * The site's anonymous id: one first-party token per browser, shared by
 * GrowthBook bucketing and analytics.
 *
 * Why a client-minted cookie rather than a proxy: bucketing happens entirely
 * in the browser (the server only ships feature *definitions*, see
 * `growthbook/server.ts`), so no server render needs this id. Minting it
 * client-side keeps the admin-only `proxy.ts` matcher untouched and avoids
 * putting a `Set-Cookie` on every marketing response.
 *
 * It must be **stable across visits**. An experiment re-buckets a returning
 * visitor whose id changed, so its exposure counts stop matching its conversion
 * counts; analytics loses the pre-signup half of every funnel the same way,
 * because the anonymous sessions no longer join to the account they became.
 * Hence a long-lived cookie rather than sessionStorage.
 *
 * This is a random opaque token with no personal data in it. It is first-party,
 * `SameSite=Lax`, and readable by JS by design (both consumers run in the page).
 * It IS sent to the API on OTP verify and campaign claim so the server can link
 * this browser to the account it became — that link is the whole point of a
 * shared id, and the reason the value must stay opaque.
 */

export const DEVICE_ID_COOKIE = 'kv_did'

/**
 * Two years. Attribution windows run months, not weeks: a visitor who first
 * lands from an ad and subscribes a year later is exactly the journey this id
 * exists to stitch together, and a 1-year cookie drops it at the point the
 * answer gets interesting.
 */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 730

/**
 * The exact shape {@link mintDeviceId} produces. A stored value is checked
 * against it before being trusted: the cookie is readable AND writable by any
 * script on the origin, and this value is reported to GA as `anon_id`, so an
 * unvalidated read is a path for arbitrary text — an email address included —
 * to reach an analytics platform whose terms forbid personal data.
 */
const DEVICE_ID_PATTERN = /^[0-9a-f]{32}$/

/**
 * The id minted for this page load when the cookie cannot hold one.
 *
 * Without it every caller re-mints: `gaEvent` asks for the id on EVERY event,
 * and OTP verify and the campaign claim ask again — so a browser that refuses
 * cookies would stamp a different `anon_id` on each event and send yet another
 * to the server, turning one visitor into a crowd of one-event strangers and
 * making the device link point at none of them.
 */
let inMemoryDeviceId: string | null = null

/** Opaque hex token — no ambient meaning, nothing derived from the visitor. */
function mintDeviceId(): string {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    // Non-crypto fallback for ancient/locked-down browsers. Bucketing quality
    // degrades gracefully; nothing security-sensitive depends on this value.
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? match[1] : null
}

/**
 * Read the device id, minting and persisting one on first visit. Returns an
 * empty string during SSR — every caller runs in the browser, and an empty id
 * would otherwise put every server render in the same bucket. Callers treat the
 * empty string as "no id": it is never sent to GA, the Pixel, or the API.
 *
 * A failed write (cookies blocked, Safari private mode) is not fatal: the id is
 * still returned so the visit buckets and reports consistently *within* this
 * page load, it just won't persist to the next one. Silently degrading beats
 * throwing inside a render.
 */
export function getOrCreateDeviceId(): string {
  if (typeof document === 'undefined') return ''

  const existing = readCookie(DEVICE_ID_COOKIE)
  // A value that is not one of ours is replaced rather than repaired: it was
  // not minted here, so there is nothing in it worth carrying forward.
  if (existing && DEVICE_ID_PATTERN.test(existing)) return existing

  // Reuse the id this page load already minted instead of making another, then
  // re-attempt to persist it — the cookie may have been unwritable a moment ago
  // and writable now, and re-trying costs nothing when it already succeeded.
  const id = inMemoryDeviceId ?? mintDeviceId()
  inMemoryDeviceId = id
  try {
    const secure =
      typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${DEVICE_ID_COOKIE}=${id}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  } catch (cause) {
    reportUnlinkedException(cause, 'lib/identity/device-id.ts')
    console.warn('[identity] could not persist device id — this visit is unlinked:', cause)
  }
  return id
}

/**
 * Forget the page-load fallback id. Module state survives between tests in one
 * file, so without this a suite that clears cookies keeps handing back the id
 * an earlier test minted.
 */
export function __resetDeviceIdMemoForTests(): void {
  inMemoryDeviceId = null
}
